import { Box, useMediaQuery, useTheme } from '@mui/material'
import 'mapbox-gl/dist/mapbox-gl.css'
import '../../styles/MapBoxStyles.css'
import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  ForwardRefExoticComponent,
  RefAttributes,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useWellMapEffects, useWellMapInitialization } from '../../hooks/map'
import { useMapEffects, useMapInitialization } from '../../hooks/map'
import { AmericanStatesSelector } from '../../store/AmericanStates'
import { WaterSystemsAggregateSelector, WaterSystemCoordinatesSelector, MapWaterSystemsMetricsSelector } from '../../store/publicWaterSystem'
import { WaterSystemsSelector, WaterSystemType } from '../../store/waterSystems'
import { WellWaterSystemsAggregatesSelector} from '../../store/wellWaterSystem'
import { PublicMapRefs, WellMapRefs } from '../../types/Map'
import { CountySelector } from '../../store/County'
import { MapSelector } from '../../store/Map'
import { AddressSelector } from '../../store/Address'
import { MapRefProps } from '../../views'

type MapConfig = {
  public: {
    refs: PublicMapRefs
    effectsHook: typeof useMapEffects
    initialization: ReturnType<typeof useMapInitialization>
    data: { regionsViolations: any }
  }
  well: {
    refs: WellMapRefs
    effectsHook: typeof useWellMapEffects
    initialization: ReturnType<typeof useWellMapInitialization>
    data: { regionsHousingUnits: any }
  }
}


export const MapSection: ForwardRefExoticComponent<RefAttributes<MapRefProps>> = forwardRef((props, ref) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const dispatch = useDispatch()
  /* SELECTORS */
  const boundingBox = useSelector(WaterSystemCoordinatesSelector.selectBoundingBox)
  const coordinates = useSelector(WaterSystemCoordinatesSelector.selectWaterSystemCoordinates)

  const mapCenter = useSelector(MapSelector.getMapCenter)
  const regionsHousingUnits =    useSelector(WellWaterSystemsAggregatesSelector.selectRegionsHousingUnits) || []
  const regionsViolations = useSelector(WaterSystemsAggregateSelector.selectRegionsViolations) || []
  const selectedAddress = useSelector(AddressSelector.selectAddress)
  const selectedCounty = useSelector(CountySelector.selectCounty)
  const selectedState = useSelector(AmericanStatesSelector.getSelectedState)
  const states = useSelector(AmericanStatesSelector.makeSortedStates)
  const waterSystemMapMetrics = useSelector(MapWaterSystemsMetricsSelector.selectMapWaterSystemsData)
  const waterSystemType = useSelector(WaterSystemsSelector.selectSelectedWaterSystemType)
  const zoomLevel = useSelector(waterSystemType === WaterSystemType.WELL ? WellWaterSystemsAggregatesSelector.selectZoomLevel : WaterSystemsAggregateSelector.selectZoomLevel)
  const zoomDetail = useSelector(MapSelector.makeMapZoomDetail)
  /* REFS */
  const countyHousingUnitsRef = useRef<any>()
  const countyViolations = useSelector(WaterSystemsAggregateSelector.selectCountiesViolations) || []
  const countyViolationsRef = useRef<any>()
  const geoBlocksGroupUnitsRef = useRef<any>()
  const geoBlocksUnitsRef = useRef<any>()
  const mapCenterRef = useRef<any>()
  const markersRef: any = useRef([])
  const prevSelectedCode = useRef<string | null>(null)
  const prevSelectedCounty = useRef<string | null>(null)
  const prevSelectedGeoblock = useRef<string | null>(null)
  const regionsHousingUnitsRef = useRef<any>()
  const regionsViolationsRef = useRef<any>()
  const selectedAddressRef = useRef<any>()
  const selectedStateRef = useRef<any>()
  const statesRef = useRef<any>()
  const waterSystemMapMetricsRef = useRef<any>()
  const wellWaterSystemMapMetricsRef = useRef<any>()


  const mapConfigurations: MapConfig = {
    public: {
      initialization: useMapInitialization({
        regionsViolationsRef,
        prevSelectedCode, 
        prevSelectedCounty,
        countyViolationsRef,
        markersRef,
        dispatch,
        waterSystemType,
        waterSystemMapMetricsRef,
        statesRef,
        selectedStateRef,
        selectedAddressRef,
      }),
      refs: {
        regionsViolationsRef,
        countyViolationsRef,
        markersRef,
      } as PublicMapRefs,
      effectsHook: useMapEffects,
      data: { regionsViolations },
    },
    well: {
      initialization: useWellMapInitialization({
        regionsHousingUnitsRef,
        prevSelectedCode,
        prevSelectedCounty,
        prevSelectedGeoblock,
        countyHousingUnitsRef,
        geoBlocksGroupUnitsRef,
        wellWaterSystemMapMetricsRef,
        statesRef,
        selectedStateRef,
        markersRef,
        dispatch,
        waterSystemType,
      }),
      refs: {
        regionsHousingUnitsRef,
        countyHousingUnitsRef,
        geoBlocksUnitsRef,
        geoBlocksGroupUnitsRef,
        markersRef,
      } as WellMapRefs,
      effectsHook: useWellMapEffects,
      data: { regionsHousingUnits },
    },
  } as const
  const activeConfig = useRef(mapConfigurations[waterSystemType])
  //let activeConfig = mamConfigurations[waterSystemType]
  const { mapRef, mapContainerRef } = activeConfig.current.initialization
  useImperativeHandle(ref, () => ({
    map: mapRef.current,
    mapContainer: mapContainerRef.current
  }));

  useMapEffects({
    ...activeConfig.current.data,
    refs:
      waterSystemType === WaterSystemType.PUBLIC
        ? (activeConfig.current.refs as PublicMapRefs)
        : ({} as PublicMapRefs),
    regionsViolations,
    countyViolations,
    boundingBox,
    coordinates,
    selectedState,
    selectedCounty,
    selectedAddress,
    mapCenterRef,
    waterSystemMapMetricsRef,
    waterSystemMapMetrics,
    mapCenter,
    states,
    statesRef,
    selectedStateRef,
    selectedAddressRef,
    mapRef,
    prevSelectedCode,
    zoomLevel,
    dispatch,
    theme,
    isMobile,
    zoom: zoomDetail
  })

/*  useWellMapEffects({
    ...activeConfig.current.data,
    refs:
      waterSystemType === WaterSystemType.WELL  
        ? (activeConfig.current.refs as WellMapRefs)
        : ({} as WellMapRefs),
    regionsHousingUnits,
    countyHousingUnits,
    waterSystemGeoBlocks,
    coordinates: wellCoordinates,
    selectedState,
    prevSelectedCode,
    wellWaterSystemMapMetrics,
    wellWaterSystemMapMetricsRef,
    mapRef,
    zoomLevel,
    dispatch,
    theme,
    prevSelectedCounty,
    enabled: waterSystemType === WaterSystemType.WELL,
  })*/

  return (
    <Box component="div" sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />
    </Box>
  )
})
